import React, { useEffect, useState, useMemo } from "react";
import { Skeleton, Table } from "antd";
import { useSearchParams } from "react-router-dom";
import { useThunk } from "../../hooks/useThunk";

const DataTable = ({
  columns,
  fetchData,
  rowKey = "_id",
  filters = {},
  initialPageSize = 10,
  tableStyle = {},
  headingBgColor = "#DFEAE8",
  headingTextColor = "#0F161E",
  enableUrlParams = true,
  customQueryParams,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  // Use the custom useThunk hook to fetch data
  const [doFetchData, isFetchingData, fetchDataError] = useThunk(fetchData);

  // Get pagination values from URL params or use defaults
  let page = Number(searchParams.get("page")) || 1;
  const currentPageSize =
    Number(searchParams.get("pageSize")) || initialPageSize;

  // Update URL parameters and return the updated params object
  const updateUrlParams = (newParams) => {
    if (!enableUrlParams) return newParams;

    const updatedParams = new URLSearchParams(searchParams);

    // Update or remove parameters
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        updatedParams.set(key, value.toString());
      } else {
        updatedParams.delete(key);
      }
    });

    setSearchParams(updatedParams, { replace: true });
    return newParams;
  };

  useEffect(() => {
    // Create a reference to the previous filters
    const filterValues = Object.entries(filters).filter(
      ([_, value]) => value !== undefined && value !== null && value !== ""
    );

    if (filterValues.length > 0) {
      const newParams = new URLSearchParams(searchParams.toString());

      // Update filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== "") {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });

      // Reset page to 1 ONLY when filters change, not on component mount
      newParams.set("page", "1");

      // Only update if something actually changed to avoid unnecessary rerenders
      if (newParams.toString() !== searchParams.toString()) {
        setSearchParams(newParams);
      }
    }
  }, [filters]);

  // Get all current parameters (pagination, sorting, filters)
  const getCurrentParams = () => {
    const params = {
      page: page,
      pageSize: currentPageSize,
      sortField: searchParams.get("sortField") || "",
      sortOrder: searchParams.get("sortOrder") || "",
    };

    // Add all active filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params[key] = value;
      }
    });

    // Add any other search params not covered by filters
    Array.from(searchParams.entries()).forEach(([key, value]) => {
      if (
        !params[key] &&
        !["page", "pageSize", "sortField", "sortOrder"].includes(key)
      ) {
        params[key] = value;
      }
    });

    return params;
  };

  // Load data from API
  const loadData = async () => {
    try {
      setLoading(true);

      // Get current parameters
      const params = getCurrentParams();

      // Convert page & pageSize to offset & limit for API
      const apiParams = { ...params };

      // Always calculate offset based on current page
      apiParams.offset = (params.page - 1) * params.pageSize;
      apiParams.limit = params.pageSize;

      delete apiParams.page;
      delete apiParams.pageSize;

      // Apply any custom transformations
      const backendParams = customQueryParams
        ? customQueryParams(apiParams)
        : apiParams;

      // Call API
      const result = await doFetchData(backendParams);

      if (result?.success) {
        setData(result?.response?.data?.data || []);
        setTotalItems(result?.response?.data?.pagination?.totalDocuments || 0);
      } else {
        console.error("Error fetching data:", result?.error);
        setData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or when relevant params change
  useEffect(() => {
    loadData();
  }, [
    page,
    currentPageSize,
    searchParams.get("sortField"),
    searchParams.get("sortOrder"),
    JSON.stringify(filters),
  ]);

  // Handle pagination change
  const handlePaginationChange = (page, pageSize) => {
    // Update URL params directly
    updateUrlParams({
      page: page,
      pageSize: pageSize,
    });

    // Data will be loaded in the useEffect when URL params change
  };

  // Handle table sorting
  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      // Update sort params and reset to page 1
      updateUrlParams({
        sortField: sorter.field,
        sortOrder: sorter.order,
        page: 1,
      });
    } else if (searchParams.has("sortField") || searchParams.has("sortOrder")) {
      // Clear sorting if it was previously set
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("sortField");
      newParams.delete("sortOrder");
      setSearchParams(newParams);
    }
  };

  // Enhance columns with sorting capabilities
  const enhancedColumns = columns.map((col) => ({
    ...col,
    sorter: col.sortable === true ? true : false,
    sortOrder:
      searchParams.get("sortField") === col.key
        ? searchParams.get("sortOrder")
        : null,
    onCell: () => ({
      style: {
        minWidth: col.minWidth || "150px",
      },
    }),
  }));

  // Create skeleton loader for the table
  const renderSkeleton = () => {
    const skeletonRows = Array(currentPageSize)
      .fill(null)
      .map((_, index) => ({
        key: `skeleton-${index}`,
        isSkeletonRow: true,
      }));

    // Custom render function for skeleton columns
    const skeletonColumns = enhancedColumns.map((col) => ({
      ...col,
      render: (text, record) => {
        if (record.isSkeletonRow) {
          return (
            <Skeleton.Button
              active
              style={{ width: "100%", height: 20 }}
              size="small"
            />
          );
        }
        return col.render ? col.render(text, record) : text;
      },
    }));

    return (
      <Table
        columns={skeletonColumns}
        dataSource={skeletonRows}
        rowKey="key"
        pagination={{
          current: page,
          pageSize: currentPageSize,
          total: totalItems,
          align: "center",
          style: {
            display: "flex",
            justifyContent: "center",
          },
        }}
        style={{
          borderRadius: "8px",
          border: "1px solid #dde1e6",
          minWidth: "100%",
          ...tableStyle,
        }}
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  backgroundColor: headingBgColor,
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "14px",
                  color: headingTextColor,
                  letterSpacing: "0.014em",
                  padding: "12px",
                }}
              />
            ),
          },
        }}
      />
    );
  };

  // return (
  //   <div>
  //     {fetchDataError ? (
  //       <div className="flex flex-col items-center gap-4 my-8">
  //         <div className="text-light-red flex-shrink-0">
  //           <svg
  //             xmlns="http://www.w3.org/2000/svg"
  //             width="40"
  //             height="40"
  //             viewBox="0 0 24 24"
  //             fill="none"
  //             stroke="currentColor"
  //             strokeWidth="2"
  //             strokeLinecap="round"
  //             strokeLinejoin="round"
  //           >
  //             <circle cx="12" cy="12" r="10"></circle>
  //             <line x1="12" y1="8" x2="12" y2="12"></line>
  //             <line x1="12" y1="16" x2="12.01" y2="16"></line>
  //           </svg>
  //         </div>
  //         <div className="text-center md:text-left max-w-md break-words">
  //           <p className="text-light-red font-inter text-center">
  //             Error loading data: {fetchDataError?.message}
  //           </p>
  //         </div>
  //       </div>
  //     ) : (
  //       <div className="overflow-x-auto w-full">
  //         {loading ? (
  //           renderSkeleton()
  //         ) : (
  //           <Table
  //             columns={enhancedColumns}
  //             dataSource={data}
  //             rowKey={rowKey}
  //             loading={false} // We're handling loading state with skeleton now
  //             onChange={handleTableChange}
  //             pagination={{
  //               current: page,
  //               pageSize: currentPageSize,
  //               total: totalItems,
  //               onChange: handlePaginationChange,
  //               showSizeChanger: false,
  //               // showTotal: (total) => `Total ${total} items`,
  //               align: "center",
  //               style: {
  //                 display: "flex",
  //                 justifyContent: "center",
  //               },
  //             }}
  //             style={{
  //               borderRadius: "8px",
  //               border: "1px solid #dde1e6",
  //               minWidth: "100%",
  //               ...tableStyle,
  //             }}
  //             components={{
  //               header: {
  //                 cell: (props) => (
  //                   <th
  //                     {...props}
  //                     style={{
  //                       backgroundColor: headingBgColor,
  //                       fontFamily: "Inter",
  //                       fontWeight: 600,
  //                       fontSize: "14px",
  //                       color: headingTextColor,
  //                       letterSpacing: "0.014em",
  //                       padding: "12px",
  //                     }}
  //                   />
  //                 ),
  //               },
  //             }}
  //           />
  //         )}
  //       </div>
  //     )}
  //   </div>
  // );
  return (
    <div>
      {loading ? (
        renderSkeleton()
      ) : fetchDataError ? (
        <div className="flex flex-col items-center gap-4 my-8">
          <div className="text-light-red flex-shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <div className="text-center md:text-left max-w-md break-words">
            <p className="text-light-red font-inter text-center">
              Error loading data: {fetchDataError?.message}
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto w-full">
          <Table
            columns={enhancedColumns}
            dataSource={data}
            rowKey={rowKey}
            loading={false}
            onChange={handleTableChange}
            pagination={{
              current: page,
              pageSize: currentPageSize,
              total: totalItems,
              onChange: handlePaginationChange,
              showSizeChanger: false,
              align: "center",
              style: {
                display: "flex",
                justifyContent: "center",
              },
            }}
            style={{
              borderRadius: "8px",
              border: "1px solid #dde1e6",
              minWidth: "100%",
              ...tableStyle,
            }}
            components={{
              header: {
                cell: (props) => (
                  <th
                    {...props}
                    style={{
                      backgroundColor: headingBgColor,
                      fontFamily: "Inter",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: headingTextColor,
                      letterSpacing: "0.014em",
                      padding: "12px",
                    }}
                  />
                ),
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DataTable;
